import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ExternalLink, RefreshCw, Languages, ArrowLeft } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import { BACKEND_URL } from "../lib/backend";
import { setCanonical } from "../lib/seo";

const API = BACKEND_URL;
const PAGE_SIZE = 20;

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function OfficinaPage() {
  const location = useLocation();
  const basePage = useMemo(() => {
    const p = parseInt(new URLSearchParams(location.search).get("page"), 10);
    return Number.isFinite(p) && p > 1 ? p : 1;
  }, [location.search]);

  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dm] = useDarkMode();

  useEffect(() => {
    document.title = basePage > 1
      ? `Italian Tourism Industry Insights in English — Page ${basePage} | TourismIntel`
      : "Italian Tourism Industry Insights in English — Officina Turistica | TourismIntel";
    const desc = document.querySelector('meta[name="description"]');
    const originalDesc = desc?.getAttribute("content");
    desc?.setAttribute("content",
      "Posts from Officina Turistica, Italy's tourism-industry blog, translated into English: Italian tourism market trends, hotel industry news and travel tech insights.");
    setCanonical(`/officina-turistica${basePage > 1 ? `?page=${basePage}` : ""}`);
    setLoading(true);
    axios.get(`${API}/api/officina/articles?limit=${PAGE_SIZE}&skip=${(basePage - 1) * PAGE_SIZE}`)
      .then(({ data }) => { setArticles(data.articles || []); setTotal(data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => {
      if (originalDesc) desc?.setAttribute("content", originalDesc);
    };
  }, [basePage]);

  useEffect(() => {
    if (!articles.length) return;
    const origin = window.location.origin;
    const data = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "@id": `${origin}/officina-turistica`,
      name: "Italian Tourism Industry Insights in English — Officina Turistica",
      description: "Posts from Officina Turistica, Italy's tourism-industry blog, translated into English: Italian tourism market trends, hotel industry news and travel tech insights.",
      url: `${origin}/officina-turistica${basePage > 1 ? `?page=${basePage}` : ""}`,
      inLanguage: "en",
      blogPost: articles.map((a) => ({
        "@type": "BlogPosting",
        headline: a.title_en,
        url: `${origin}/officina-turistica/${a.slug}`,
        ...(a.published_at ? { datePublished: a.published_at } : {}),
        ...(a.image_url ? { image: a.image_url } : {}),
        author: { "@type": "Person", name: a.author || "Officina Turistica" },
        inLanguage: "en",
      })),
    };
    let el = document.getElementById("officina-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "officina-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { document.getElementById("officina-jsonld")?.remove(); };
  }, [articles, basePage]);

  const nextSkip = (basePage - 1) * PAGE_SIZE + articles.length;
  const nextPage = Math.floor(nextSkip / PAGE_SIZE) + 1;

  const loadMore = (e) => {
    e.preventDefault();
    setLoadingMore(true);
    axios.get(`${API}/api/officina/articles?limit=${PAGE_SIZE}&skip=${nextSkip}`)
      .then(({ data }) => {
        setArticles((prev) => [...prev, ...(data.articles || [])]);
        setTotal(data.total || 0);
        window.history.replaceState(null, "", `/officina-turistica?page=${nextPage}`);
        setCanonical(`/officina-turistica?page=${nextPage}`);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const c = dm
    ? { bg: "bg-[#0f1718]", card: "bg-[#1a2324]", border: "border-slate-700/50", text: "text-slate-200", muted: "text-slate-500", sub: "text-slate-400", accent: "text-cyan-400" }
    : { bg: "bg-[#f5f6f7]", card: "bg-white", border: "border-[#e7e8e9]", text: "text-[#191c1d]", muted: "text-[#6b7a7d]", sub: "text-[#3b494c]", accent: "text-[#006874]" };

  return (
    <div className={`min-h-screen flex flex-col ${c.bg}`}>
      <PublicPageNav />
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-10" data-testid="officina-page">
        <div className="mb-10">
          <div className={`flex items-center gap-2 mb-3 ${c.accent}`}>
            <Languages className="w-4 h-4" />
            <span className="category-tag">Translated from Italian</span>
          </div>
          <h1 className={`editorial-heading text-4xl sm:text-5xl font-medium mb-4 ${c.text}`}>Officina Turistica</h1>
          <p className={`text-base max-w-2xl ${c.sub}`}>
            Selected posts from <a href="https://www.officinaturistica.com" target="_blank" rel="noreferrer" className={`underline underline-offset-2 ${c.accent}`}>Officina Turistica</a>,
            the Italian tourism-industry blog — market trends, hotel industry news and travel tech from Italy,
            translated into English while preserving each author&apos;s voice. New posts are imported daily.
          </p>
        </div>

        {basePage > 1 && (
          <div className="mb-6">
            <Link to={basePage > 2 ? `/officina-turistica?page=${basePage - 1}` : "/officina-turistica"}
              data-testid="officina-newer-posts-link"
              className={`inline-flex items-center gap-1.5 text-sm font-semibold ${c.accent} hover:underline underline-offset-2`}>
              <ArrowLeft className="w-3.5 h-3.5" /> Newer posts
            </Link>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><RefreshCw className={`w-6 h-6 animate-spin ${c.muted}`} /></div>
        ) : articles.length === 0 ? (
          <p className={`text-sm text-center py-20 ${c.muted}`} data-testid="officina-empty-state">
            {basePage > 1
              ? <>No posts on this page — <Link to="/officina-turistica" className={`underline underline-offset-2 ${c.accent}`}>back to the latest posts</Link>.</>
              : "No translated posts yet — check back soon."}
          </p>
        ) : (
          <div className="space-y-6">
            {articles.map((a) => (
              <article
                key={a.slug}
                data-testid={`officina-card-${a.slug}`}
                className={`rounded-2xl border overflow-hidden transition-shadow hover:shadow-lg ${c.card} ${c.border}`}
              >
                <div className="flex flex-col sm:flex-row">
                  {a.image_url && (
                    <Link to={`/officina-turistica/${a.slug}`} className="sm:w-56 flex-shrink-0">
                      <img src={a.image_url} alt="" loading="lazy" className="w-full h-44 sm:h-full object-cover"
                        onError={(e) => { const w = e.target.closest("a"); if (w) w.style.display = "none"; }} />
                    </Link>
                  )}
                  <div className="p-5 flex-1 min-w-0">
                    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 mb-2 text-xs ${c.muted}`}>
                      <span className={`font-semibold ${c.accent}`} data-testid="officina-card-author">{a.author}</span>
                      <span>{fmtDate(a.published_at)}</span>
                      {(a.categories || []).slice(0, 3).map((cat) => (
                        <span key={cat} className={`px-2 py-0.5 rounded-full border ${c.border}`}>{cat}</span>
                      ))}
                    </div>
                    <Link to={`/officina-turistica/${a.slug}`} data-testid="officina-card-title-link">
                      <h2 className={`editorial-heading text-lg md:text-lg font-medium mb-2 hover:underline underline-offset-2 ${c.text}`}>{a.title_en}</h2>
                    </Link>
                    <p className={`text-sm mb-3 ${c.sub}`}>{a.summary_en}</p>
                    <div className="flex items-center gap-4 text-xs">
                      <Link to={`/officina-turistica/${a.slug}`} className={`font-semibold ${c.accent}`} data-testid="officina-card-read-link">
                        Read in English →
                      </Link>
                      <a href={a.url} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1 ${c.muted} hover:underline`} data-testid="officina-card-original-link">
                        Original (IT) <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            ))}
            {nextSkip < total && (
              <div className="flex justify-center pt-2">
                <a
                  href={`/officina-turistica?page=${nextPage}`}
                  onClick={loadMore}
                  aria-disabled={loadingMore}
                  data-testid="officina-load-more-btn"
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${loadingMore ? "opacity-50 pointer-events-none" : ""} ${dm ? "border-slate-600 text-slate-200 hover:bg-white/5" : "border-[#c6c8c9] text-[#191c1d] hover:bg-black/5"}`}
                >
                  {loadingMore ? "Loading…" : `Load more (${total - nextSkip} older posts)`}
                </a>
              </div>
            )}
          </div>
        )}
      </main>
      <PublicPageFooter />
    </div>
  );
}
